// Audiotool app configuration — which OAuth app, which loopback port, which
// scopes. Environment first (extension/.env in dev), then the registered
// defaults. Deliberately NOT channel-scoped: the Seedbed channel selects a
// Seedbed endpoint and must never select behaviour (config/channel.ts), and
// Audiotool is one service either way.

/** The "Seedbed" app at developer.audiotool.com/applications. A PKCE client
 * id is public — only websites at its registered redirect URIs can use it. */
export const DEFAULT_CLIENT_ID = "3ec870a7-e051-4356-b5d5-9d849320820f";

/** Registered redirect: `http://127.0.0.1:8910/`, trailing slash included. */
export const DEFAULT_REDIRECT_PORT = 8910;

/**
 * Every scope the bridge uses. Each must also be ASSIGNED to the app in the
 * dashboard, or the authorize step rejects the request; and a cached token
 * never gains scopes retroactively — re-login after changing this list.
 *   sample:write — samples.upload (a project:write-only token is refused
 *                  "[permission_denied] insufficient rights")
 *   offline      — issues a refresh token, so a session outlives the ~1 h
 *                  access token
 */
export const DEFAULT_SCOPES: readonly string[] = [
  "user:read",
  "project:read",
  "project:write",
  "offline",
  "sample:read",
  "sample:write",
  "sample:delete",
  "preset:read",
  "preset:write",
  "preset:delete",
  "playlist:read",
  "playlist:write",
  "playlist:delete",
  "favorite:write",
  "favorite:delete",
];

export interface AudiotoolConfig {
  clientId: string;
  port: number;
  redirectUri: string;
  scope: string;
  /** A personal access token (dev only). When set, OAuth is skipped entirely. */
  pat: string | null;
}

type Env = Record<string, string | undefined>;

export function audiotoolConfig(env: Env = process.env): AudiotoolConfig {
  const clientId = env["AUDIOTOOL_CLIENT_ID"]?.trim() || DEFAULT_CLIENT_ID;
  const parsedPort = Number(env["AUDIOTOOL_REDIRECT_PORT"]);
  const port = Number.isInteger(parsedPort) && parsedPort > 0 && parsedPort < 65536 ? parsedPort : DEFAULT_REDIRECT_PORT;
  const scope = env["AUDIOTOOL_SCOPE"]?.trim() || DEFAULT_SCOPES.join(" ");
  const pat = env["AUDIOTOOL_PAT"]?.trim() || null;
  return { clientId, port, redirectUri: `http://127.0.0.1:${port}/`, scope, pat };
}
